// Thành phần nhập liệu. Giá trị điều khiển hoàn toàn từ ngoài (controlled), trả giá trị đã chuẩn hoá. Mọi ô nhập nhận thêm
// thuộc tính của phần tử gốc (aria-*, id, autoFocus, disabled, onBlur…) và `ref` (React 19: ref là prop). Đặt trong Field ⇒
// tự gắn aria-describedby tới dòng lỗi / gợi ý và aria-invalid khi Field có lỗi.
import { createContext, useContext, useEffect, useId, useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cx } from '../lib/cx';
import { fmtMoney, fmtNum } from '../lib/format';

interface FieldCtx {
  /** id của dòng lỗi (khi có lỗi) hoặc gợi ý. */
  describedBy?: string;
  invalid: boolean;
}
const FieldContext = createContext<FieldCtx | null>(null);

/** aria-describedby / aria-invalid của ô nhập theo Field bao ngoài (gộp với giá trị ô tự truyền). */
function useFieldAria(p: { 'aria-describedby'?: string; 'aria-invalid'?: ComponentPropsWithRef<'input'>['aria-invalid'] }, invalid?: boolean) {
  const f = useContext(FieldContext);
  const describedBy = [p['aria-describedby'], f?.describedBy].filter(Boolean).join(' ') || undefined;
  const ariaInvalid = p['aria-invalid'] ?? (invalid || f?.invalid ? true : undefined);
  return { 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid };
}

export function Field({ label, hint, error, req, children, className, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; req?: boolean; children: ReactNode; className?: string; htmlFor?: string }) {
  const id = useId();
  const msgId = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cx('field', className)}>
      {label && (
        <label className={req ? 'req' : undefined} htmlFor={htmlFor}>
          {label}
        </label>
      )}
      <FieldContext.Provider value={{ describedBy: msgId, invalid: !!error }}>{children}</FieldContext.Provider>
      {error ? (
        <span className="err" id={msgId}>
          {error}
        </span>
      ) : hint ? (
        <span className="hint" id={msgId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

type InputProps = Omit<ComponentPropsWithRef<'input'>, 'value' | 'onChange'> & { value: string | null | undefined; onChange?: (v: string) => void; invalid?: boolean };
export function Input({ value, onChange, className, invalid, ...p }: InputProps) {
  const aria = useFieldAria(p, invalid);
  return <input className={cx('input', (invalid || aria['aria-invalid'] === true) && 'invalid', className)} value={value ?? ''} onChange={(e) => onChange?.(e.currentTarget.value)} {...p} {...aria} />;
}

type TextareaProps = Omit<ComponentPropsWithRef<'textarea'>, 'value' | 'onChange'> & { value: string | null | undefined; onChange?: (v: string) => void };
export function Textarea({ value, onChange, className, ...p }: TextareaProps) {
  const aria = useFieldAria(p);
  return <textarea className={cx('textarea', className)} value={value ?? ''} onChange={(e) => onChange?.(e.currentTarget.value)} {...p} {...aria} />;
}

export type Option = string | [value: string, label: string];
type SelectProps = Omit<ComponentPropsWithRef<'select'>, 'value' | 'onChange' | 'children'> & { value: string | null | undefined; onChange: (v: string) => void; options: Option[]; placeholder?: string };
export function Select({ value, onChange, options, placeholder, className, ...p }: SelectProps) {
  const aria = useFieldAria(p);
  return (
    <select className={cx('select', className)} value={value ?? ''} onChange={(e) => onChange(e.currentTarget.value)} {...p} {...aria}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) =>
        Array.isArray(o) ? (
          <option key={o[0]} value={o[0]}>
            {o[1]}
          </option>
        ) : (
          <option key={o} value={o}>
            {o}
          </option>
        ),
      )}
    </select>
  );
}

/** Thuộc tính của <input> gốc mà Money / NumInput chuyển tiếp (giá trị, kiểu, onChange do ô tự quản). */
type NumberInputAttrs = Omit<ComponentPropsWithRef<'input'>, 'value' | 'onChange' | 'type' | 'className' | 'inputMode' | 'step' | 'min' | 'max' | 'children'>;

/** Ô nhập tiền: hiển thị phân tách hàng nghìn, trả về số nguyên VND. */
export function Money({ value, onChange, suffix = '₫', invalid, ...p }: NumberInputAttrs & { value: number | null | undefined; onChange: (n: number) => void; suffix?: string; invalid?: boolean }) {
  const [txt, setTxt] = useState(value ? fmtNum(value) : '');
  const aria = useFieldAria(p, invalid);
  useEffect(() => {
    const cur = Number(String(txt).replace(/\D/g, '')) || 0;
    if (cur !== (value || 0)) setTxt(value ? fmtNum(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="col gap-4">
      <div className={cx('input-group', aria['aria-invalid'] === true && 'invalid')}>
        <input
          {...p}
          {...aria}
          className="input num"
          inputMode="numeric"
          value={txt}
          onChange={(e) => {
            const n = Number(e.currentTarget.value.replace(/\D/g, '')) || 0;
            setTxt(n ? fmtNum(n) : '');
            onChange(n);
          }}
        />
        <span className="suffix">{suffix}</span>
      </div>
      {(value ?? 0) >= 1e6 && <span className="tiny muted">= {fmtMoney(value)}</span>}
    </div>
  );
}

export function NumInput({ value, onChange, suffix, step = 'any', min, max, invalid, ...p }: NumberInputAttrs & { value: number | null | undefined; onChange: (n: number | null) => void; suffix?: string; step?: string | number; min?: number; max?: number; invalid?: boolean }) {
  const aria = useFieldAria(p, invalid);
  return (
    <div className={cx('input-group', aria['aria-invalid'] === true && 'invalid')}>
      <input {...p} {...aria} className="input num" type="number" step={step} min={min} max={max} value={value ?? ''} onChange={(e) => onChange(e.currentTarget.value === '' ? null : Number(e.currentTarget.value))} />
      {suffix && <span className="suffix">{suffix}</span>}
    </div>
  );
}

export function Check({ checked, onChange, children, id, disabled }: { checked: boolean; onChange: (v: boolean) => void; children?: ReactNode; id?: string; disabled?: boolean }) {
  return (
    <label className="check" htmlFor={id}>
      <input id={id} type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.currentTarget.checked)} />
      <span>{children}</span>
    </label>
  );
}

export function Seg<K extends string>({ value, onChange, options }: { value: K; onChange: (k: K) => void; options: [K, ReactNode][] }) {
  return (
    <div className="seg" role="tablist">
      {options.map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} className={value === k ? 'on' : ''} onClick={() => onChange(k)}>
          {label}
        </button>
      ))}
    </div>
  );
}
